// components/layout/Footer.jsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import {
    Mail,
    Phone,
    MapPin,
    Clock,
    Shield,
    Truck,
    CreditCard,
    Headphones,
    ArrowRight,
    ChevronRight,
    Sparkles,
    Award,
    Globe,
    Smartphone,
    Package,
    ShoppingBag,
    Wifi,
    Store,
    Users,
    Heart,
    Zap
} from 'lucide-react';

// Custom social icons using SVG since Lucide doesn't have them
const SocialIcons = {
    Facebook: (props) => (
        <svg viewBox="0 0 24 24" width={props.size || 20} height={props.size || 20} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
        </svg>
    ),
    Twitter: (props) => (
        <svg viewBox="0 0 24 24" width={props.size || 20} height={props.size || 20} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z"/>
        </svg>
    ),
    Instagram: (props) => (
        <svg viewBox="0 0 24 24" width={props.size || 20} height={props.size || 20} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
        </svg>
    ),
    Youtube: (props) => (
        <svg viewBox="0 0 24 24" width={props.size || 20} height={props.size || 20} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19.1c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.43z"/>
            <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
        </svg>
    ),
    Linkedin: (props) => (
        <svg viewBox="0 0 24 24" width={props.size || 20} height={props.size || 20} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/>
            <rect x="2" y="9" width="4" height="12"/>
            <circle cx="4" cy="4" r="2"/>
        </svg>
    )
};

const Footer = () => {
    const { colors } = useTheme();
    const navigate = useNavigate();
    const currentYear = new Date().getFullYear();

    const quickLinks = [
        { label: 'Data Packages', path: '/data', icon: Wifi },
        { label: 'Marketplace', path: '/marketplace', icon: Store },
        { label: 'Dropshipping', path: '/dropshipping', icon: Package },
        { label: 'Pre-Orders', path: '/preorder', icon: ShoppingBag },
    ];

    const supportLinks = [
        { label: 'Help Center', path: '/help' },
        { label: 'Terms of Service', path: '/terms' },
        { label: 'Privacy Policy', path: '/privacy' },
        { label: 'Refund Policy', path: '/refund' },
    ];

    const socialLinks = [
        { icon: SocialIcons.Facebook, label: 'Facebook', url: 'https://facebook.com/azoemart' },
        { icon: SocialIcons.Twitter, label: 'Twitter', url: 'https://twitter.com/azoemart' },
        { icon: SocialIcons.Instagram, label: 'Instagram', url: 'https://instagram.com/azoemart' },
        { icon: SocialIcons.Youtube, label: 'YouTube', url: 'https://youtube.com/azoemart' },
        { icon: SocialIcons.Linkedin, label: 'LinkedIn', url: 'https://linkedin.com/company/azoemart' },
    ];

    const features = [
        { icon: Truck, label: 'Fast Delivery', description: 'Instant data & prompt logistics' },
        { icon: Shield, label: 'Secure Payment', description: '100% safe & encrypted' },
        { icon: Headphones, label: '24/7 Support', description: 'Always here to help' },
        { icon: Award, label: 'Best Prices', description: 'Unbeatable market rates' },
    ];

    return (
        <footer style={{
            backgroundColor: colors.raisedBg,
            borderTop: `1px solid ${colors.border}`,
            marginTop: '40px',
            color: colors.secondaryText,
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Decorative Elements */}
            <div style={{
                position: 'absolute',
                top: -100,
                right: -100,
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${colors.btnBg}05 0%, transparent 70%)`,
                pointerEvents: 'none',
            }} />
            <div style={{
                position: 'absolute',
                bottom: -80,
                left: -80,
                width: '200px',
                height: '200px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${colors.success}05 0%, transparent 70%)`,
                pointerEvents: 'none',
            }} />

            {/* Main Footer Content */}
            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
                padding: '48px 24px 24px',
                position: 'relative',
                zIndex: 1,
            }}>
                {/* Features Row */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '20px',
                    marginBottom: '40px',
                    paddingBottom: '40px',
                    borderBottom: `1px solid ${colors.border}`,
                }}>
                    {features.map((feature, idx) => {
                        const Icon = feature.icon;
                        return (
                            <div key={idx} style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '14px',
                                padding: '8px 4px',
                            }}>
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '12px',
                                    backgroundColor: `${colors.btnBg}10`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: colors.btnBg,
                                    flexShrink: 0,
                                }}>
                                    <Icon size={22} />
                                </div>
                                <div>
                                    <div style={{
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        color: colors.heading,
                                        marginBottom: '2px',
                                    }}>
                                        {feature.label}
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: colors.secondaryText,
                                    }}>
                                        {feature.description}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Main Footer Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '40px',
                    marginBottom: '40px',
                }}>
                    {/* Brand Column */}
                    <div style={{
                        minWidth: '200px',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '14px',
                        }}>
                            <div style={{
                                width: '40px',
                                height: '40px',
                                borderRadius: '12px',
                                background: `linear-gradient(135deg, ${colors.btnBg}, ${colors.btnBg}88)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#fff',
                                fontWeight: '800',
                                fontSize: '18px',
                                flexShrink: 0,
                                boxShadow: `0 4px 12px ${colors.btnBg}33`,
                            }}>
                                A
                            </div>
                            <span style={{
                                fontSize: '22px',
                                fontWeight: '800',
                                color: colors.heading,
                                letterSpacing: '-0.5px',
                            }}>
                                Azoe<span style={{ color: colors.btnBg }}>mart</span>
                            </span>
                        </div>

                        <p style={{
                            margin: '0 0 16px 0',
                            fontSize: '14px',
                            lineHeight: '1.7',
                            color: colors.secondaryText,
                            maxWidth: '320px',
                        }}>
                            Your trusted destination for affordable data bundles, marketplace goods, and group pre-orders across Ghana.
                        </p>

                        <div style={{
                            display: 'flex',
                            gap: '10px',
                            flexWrap: 'wrap',
                        }}>
                            {socialLinks.map((social, idx) => {
                                const Icon = social.icon;
                                return (
                                    <a
                                        key={idx}
                                        href={social.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={social.label}
                                        style={{
                                            width: '40px',
                                            height: '40px',
                                            borderRadius: '10px',
                                            backgroundColor: `${colors.btnBg}10`,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: colors.secondaryText,
                                            transition: 'all 0.3s ease',
                                            textDecoration: 'none',
                                            border: `1px solid ${colors.border}`,
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = colors.btnBg;
                                            e.currentTarget.style.color = '#fff';
                                            e.currentTarget.style.transform = 'translateY(-3px)';
                                            e.currentTarget.style.boxShadow = `0 4px 16px ${colors.btnBg}44`;
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = `${colors.btnBg}10`;
                                            e.currentTarget.style.color = colors.secondaryText;
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }}
                                    >
                                        <Icon size={18} />
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    {/* Quick Links */}
                    <div>
                        <h4 style={{
                            fontSize: '15px',
                            fontWeight: '700',
                            color: colors.heading,
                            margin: '0 0 16px 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}>
                            <Zap size={18} color={colors.btnBg} />
                            Quick Links
                        </h4>
                        <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}>
                            {quickLinks.map((link, idx) => {
                                const Icon = link.icon;
                                return (
                                    <li key={idx}>
                                        <button
                                            onClick={() => navigate(link.path)}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                background: 'none',
                                                border: 'none',
                                                color: colors.secondaryText,
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                padding: '4px 0',
                                                transition: 'all 0.2s ease',
                                                width: '100%',
                                                textAlign: 'left',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.color = colors.btnBg;
                                                e.currentTarget.style.transform = 'translateX(4px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.color = colors.secondaryText;
                                                e.currentTarget.style.transform = 'translateX(0)';
                                            }}
                                        >
                                            <Icon size={16} />
                                            {link.label}
                                            <ChevronRight size={14} style={{
                                                marginLeft: 'auto',
                                                opacity: 0.5,
                                            }} />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Support Links */}
                    <div>
                        <h4 style={{
                            fontSize: '15px',
                            fontWeight: '700',
                            color: colors.heading,
                            margin: '0 0 16px 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}>
                            <Shield size={18} color={colors.btnBg} />
                            Support
                        </h4>
                        <ul style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px',
                        }}>
                            {supportLinks.map((link, idx) => (
                                <li key={idx}>
                                    <button
                                        onClick={() => navigate(link.path)}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            background: 'none',
                                            border: 'none',
                                            color: colors.secondaryText,
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            padding: '4px 0',
                                            transition: 'all 0.2s ease',
                                            width: '100%',
                                            textAlign: 'left',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.color = colors.btnBg;
                                            e.currentTarget.style.transform = 'translateX(4px)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.color = colors.secondaryText;
                                            e.currentTarget.style.transform = 'translateX(0)';
                                        }}
                                    >
                                        {link.label}
                                        <ChevronRight size={14} style={{
                                            marginLeft: 'auto',
                                            opacity: 0.5,
                                        }} />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact Info */}
                    <div>
                        <h4 style={{
                            fontSize: '15px',
                            fontWeight: '700',
                            color: colors.heading,
                            margin: '0 0 16px 0',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                        }}>
                            <Headphones size={18} color={colors.btnBg} />
                            Contact Us
                        </h4>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                fontSize: '14px',
                                color: colors.secondaryText,
                            }}>
                                <Mail size={18} style={{
                                    color: colors.btnBg,
                                    flexShrink: 0,
                                    marginTop: '2px',
                                }} />
                                <span>support@azoemart.com</span>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                fontSize: '14px',
                                color: colors.secondaryText,
                            }}>
                                <Phone size={18} style={{
                                    color: colors.btnBg,
                                    flexShrink: 0,
                                    marginTop: '2px',
                                }} />
                                <span>+233 50 000 0000</span>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                fontSize: '14px',
                                color: colors.secondaryText,
                            }}>
                                <MapPin size={18} style={{
                                    color: colors.btnBg,
                                    flexShrink: 0,
                                    marginTop: '2px',
                                }} />
                                <span>Accra, Ghana</span>
                            </div>
                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                fontSize: '14px',
                                color: colors.secondaryText,
                            }}>
                                <Clock size={18} style={{
                                    color: colors.btnBg,
                                    flexShrink: 0,
                                    marginTop: '2px',
                                }} />
                                <span>Mon - Fri: 8AM - 8PM</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Newsletter & App Download */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '24px',
                    padding: '24px',
                    backgroundColor: `${colors.btnBg}08`,
                    borderRadius: '16px',
                    border: `1px solid ${colors.border}`,
                    marginBottom: '40px',
                }}>
                    <div>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px',
                        }}>
                            <Mail size={20} color={colors.btnBg} />
                            <span style={{
                                fontSize: '15px',
                                fontWeight: '700',
                                color: colors.heading,
                            }}>
                                Subscribe to our newsletter
                            </span>
                        </div>
                        <p style={{
                            fontSize: '13px',
                            color: colors.secondaryText,
                            margin: '0 0 12px 0',
                        }}>
                            Get the latest deals and exclusive offers
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '8px',
                            maxWidth: '400px',
                        }}>
                            <input
                                type="email"
                                placeholder="Enter your email"
                                style={{
                                    flex: 1,
                                    padding: '10px 14px',
                                    borderRadius: '10px',
                                    border: `1px solid ${colors.border}`,
                                    backgroundColor: colors.raisedBg,
                                    color: colors.text,
                                    fontSize: '14px',
                                    outline: 'none',
                                    transition: 'all 0.2s ease',
                                    minWidth: '0',
                                }}
                                onFocus={(e) => {
                                    e.currentTarget.style.borderColor = colors.btnBg;
                                    e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.btnBg}20`;
                                }}
                                onBlur={(e) => {
                                    e.currentTarget.style.borderColor = colors.border;
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            />
                            <button style={{
                                padding: '10px 20px',
                                borderRadius: '10px',
                                border: 'none',
                                backgroundColor: colors.btnBg,
                                color: colors.btnText,
                                fontWeight: '600',
                                fontSize: '14px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                whiteSpace: 'nowrap',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.02)';
                                e.currentTarget.style.boxShadow = `0 4px 16px ${colors.btnBg}44`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = 'none';
                            }}
                            >
                                Subscribe
                                <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>

                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            marginBottom: '8px',
                        }}>
                            <Smartphone size={20} color={colors.btnBg} />
                            <span style={{
                                fontSize: '15px',
                                fontWeight: '700',
                                color: colors.heading,
                            }}>
                                Download our app
                            </span>
                        </div>
                        <p style={{
                            fontSize: '13px',
                            color: colors.secondaryText,
                            margin: '0 0 12px 0',
                        }}>
                            Get the best experience on mobile
                        </p>
                        <div style={{
                            display: 'flex',
                            gap: '10px',
                        }}>
                            <button style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                borderRadius: '10px',
                                border: `1px solid ${colors.border}`,
                                backgroundColor: colors.raisedBg,
                                color: colors.text,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontSize: '12px',
                                fontWeight: '500',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = colors.btnBg;
                                e.currentTarget.style.background = `${colors.btnBg}10`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = colors.border;
                                e.currentTarget.style.background = colors.raisedBg;
                            }}
                            >
                                <Globe size={16} />
                                App Store
                            </button>
                            <button style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '8px 16px',
                                borderRadius: '10px',
                                border: `1px solid ${colors.border}`,
                                backgroundColor: colors.raisedBg,
                                color: colors.text,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontSize: '12px',
                                fontWeight: '500',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = colors.btnBg;
                                e.currentTarget.style.background = `${colors.btnBg}10`;
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = colors.border;
                                e.currentTarget.style.background = colors.raisedBg;
                            }}
                            >
                                <Smartphone size={16} />
                                Google Play
                            </button>
                        </div>
                    </div>
                </div>

                {/* Bottom Bar */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px',
                    paddingTop: '20px',
                    borderTop: `1px solid ${colors.border}`,
                    fontSize: '13px',
                    color: colors.secondaryText,
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                    }}>
                        <Heart size={14} color={colors.error} />
                        <span>
                            &copy; {currentYear} Azoemart. All rights reserved.
                        </span>
                    </div>

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        flexWrap: 'wrap',
                    }}>
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}>
                            <Shield size={14} color={colors.success} />
                            Secure Payments
                        </span>
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}>
                            <CreditCard size={14} color={colors.warning} />
                            Paystack
                        </span>
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                        }}>
                            <Zap size={14} color={colors.linkColor} />
                            Instant Delivery
                        </span>
                        <span style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: colors.secondaryText,
                            opacity: 0.6,
                            fontSize: '11px',
                        }}>
                            v2.0.0
                        </span>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
            `}</style>
        </footer>
    );
};

export default Footer;